const MIN_READABLE_CONTRAST = 2.5;
const DEFAULT_LIGHT_TEXT = "#FFFFFF";
const DEFAULT_DARK_TEXT = "#111827";

function text(value) {
  return String(value ?? "").trim();
}

function clamp01(value, fallback = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(1, numeric));
}

function parseChannel(value) {
  const raw = text(value);
  if (!raw) return null;
  if (raw.endsWith("%")) {
    const percentage = Number(raw.slice(0, -1));
    return Number.isFinite(percentage) ? Math.round(clamp01(percentage / 100, 0) * 255) : null;
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? Math.max(0, Math.min(255, numeric)) : null;
}

function parseAlpha(value, fallback = 1) {
  const raw = text(value);
  if (!raw) return fallback;
  if (raw.endsWith("%")) {
    const percentage = Number(raw.slice(0, -1));
    return Number.isFinite(percentage) ? clamp01(percentage / 100, fallback) : fallback;
  }
  return clamp01(raw, fallback);
}

function hslToRgb(hue, saturation, lightness) {
  const h = ((((Number(hue) || 0) % 360) + 360) % 360) / 360;
  const s = clamp01(saturation, 0);
  const l = clamp01(lightness, 0);
  if (s === 0) {
    const gray = Math.round(l * 255);
    return [gray, gray, gray];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hueToRgb = (input) => {
    let value = input;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  return [
    Math.round(hueToRgb(h + 1 / 3) * 255),
    Math.round(hueToRgb(h) * 255),
    Math.round(hueToRgb(h - 1 / 3) * 255),
  ];
}

function parseCssColor(value) {
  const candidate = text(value).toLowerCase();
  if (!candidate || candidate === "currentcolor") return null;
  if (candidate === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  if (candidate === "white") return { r: 255, g: 255, b: 255, a: 1 };
  if (candidate === "black") return { r: 0, g: 0, b: 0, a: 1 };

  const hex = candidate.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let raw = hex[1];
    if (raw.length === 3 || raw.length === 4) raw = raw.split("").map((char) => `${char}${char}`).join("");
    if (![6, 8].includes(raw.length)) return null;
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
      a: raw.length === 8 ? parseInt(raw.slice(6, 8), 16) / 255 : 1,
    };
  }

  const rgb = candidate.match(/^rgba?\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^,\)]+)(?:\s*,\s*([^\)]+))?\s*\)$/i);
  if (rgb) {
    const r = parseChannel(rgb[1]);
    const g = parseChannel(rgb[2]);
    const b = parseChannel(rgb[3]);
    if ([r, g, b].some((channel) => channel === null)) return null;
    return { r, g, b, a: parseAlpha(rgb[4], 1) };
  }

  const hsl = candidate.match(/^hsla?\(\s*([0-9.]+)(?:deg)?\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%(?:\s*,\s*([^\)]+))?\s*\)$/i);
  if (hsl) {
    const [r, g, b] = hslToRgb(Number(hsl[1]), Number(hsl[2]) / 100, Number(hsl[3]) / 100);
    return { r, g, b, a: parseAlpha(hsl[4], 1) };
  }

  return null;
}

function explicitAlpha(value) {
  const candidate = text(value).toLowerCase();
  return candidate === "transparent" || /^#[0-9a-f]{4}$/i.test(candidate) || /^#[0-9a-f]{8}$/i.test(candidate) || /^rgba\(/i.test(candidate) || /^hsla\(/i.test(candidate);
}

function composite(foreground, background) {
  if (!foreground || !background) return null;
  const alpha = Math.max(0, Math.min(1, foreground.a));
  return {
    r: foreground.r * alpha + background.r * (1 - alpha),
    g: foreground.g * alpha + background.g * (1 - alpha),
    b: foreground.b * alpha + background.b * (1 - alpha),
    a: 1,
  };
}

function opaqueCss(color) {
  if (!color) return "";
  return `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
}

function channelLuminance(channel) {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function luminance(color) {
  return 0.2126 * channelLuminance(color.r) +
    0.7152 * channelLuminance(color.g) +
    0.0722 * channelLuminance(color.b);
}

function contrastRatio(foregroundValue, backgroundValue) {
  const background = parseCssColor(backgroundValue);
  const foreground = parseCssColor(foregroundValue);
  if (!background || !foreground) return null;
  const opaqueBackground = background.a < 1
    ? composite(background, { r: 255, g: 255, b: 255, a: 1 })
    : background;
  const opaqueForeground = foreground.a < 1 ? composite(foreground, opaqueBackground) : foreground;
  if (!opaqueBackground || !opaqueForeground) return null;
  const light = Math.max(luminance(opaqueForeground), luminance(opaqueBackground));
  const dark = Math.min(luminance(opaqueForeground), luminance(opaqueBackground));
  return (light + 0.05) / (dark + 0.05);
}

function ensureReadableTextColor(foreground, background, minimumContrast = MIN_READABLE_CONTRAST) {
  const current = contrastRatio(foreground, background);
  if (current === null || current >= minimumContrast) return foreground;
  const lightRatio = contrastRatio(DEFAULT_LIGHT_TEXT, background) || 0;
  const darkRatio = contrastRatio(DEFAULT_DARK_TEXT, background) || 0;
  return lightRatio >= darkRatio ? DEFAULT_LIGHT_TEXT : DEFAULT_DARK_TEXT;
}

function paletteValue(colors, key, fallback) {
  return text(colors?.[key]) || fallback;
}

function elementStyle(visualStyle, path) {
  const value = visualStyle?.elementColors?.[path];
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function localPalette(visualStyle, fallback) {
  if (visualStyle?.useGlobalColors !== false) return null;
  const colors = visualStyle?.colors || {};
  const background = paletteValue(colors, "background", fallback.background);
  const primary = paletteValue(colors, "primary", fallback.primary);
  const secondary = paletteValue(colors, "secondary", fallback.secondary);
  const surface = paletteValue(colors, "surface", fallback.surface);
  const textColor = paletteValue(colors, "text", fallback.text);
  const mutedText = paletteValue(colors, "mutedText", fallback.mutedText);
  const onPrimary = paletteValue(colors, "onPrimary", fallback.onPrimary);
  const onSecondary = paletteValue(colors, "onSecondary", fallback.onSecondary);
  // Para los hijos de una card, la paleta local se resuelve primero sin
  // corregirla contra el fondo de la sección. La barrera final la evalúa
  // contra la superficie real de la card (o del CTA) para evitar que una
  // sección oscura fuerce texto claro dentro de una card clara, y viceversa.
  return {
    background,
    primary,
    secondary,
    surface,
    text: textColor,
    mutedText,
    onPrimary,
    onSecondary,
  };
}

function effectiveCardSurface({
  cardConfig,
  palette,
  visualStyle,
  cardBackgroundImageVisible,
  sectionBackgroundColor,
  sectionBackgroundComplex,
  sectionHasBackgroundImage,
}) {
  if (cardBackgroundImageVisible) return { resolved: false, color: "", explicit: true, reason: "card-image" };

  const local = localPalette(visualStyle, palette);
  const surfaceOverride = text(elementStyle(visualStyle, "catalog.cards.surface").background);
  const localSurface = text(local?.surface);
  const configured = text(cardConfig?.backgroundColor);
  const source = surfaceOverride || localSurface || configured || palette.surface;
  const parsed = parseCssColor(source);
  if (!parsed) return { resolved: false, color: "", explicit: Boolean(surfaceOverride || localSurface || configured), reason: "unparseable" };

  let foreground = { ...parsed };
  const opacityApplies = !surfaceOverride && !localSurface && !explicitAlpha(source);
  if (opacityApplies) foreground.a = clamp01(cardConfig?.backgroundOpacity, 1);

  if (foreground.a >= 0.999) {
    return {
      resolved: true,
      color: opaqueCss({ ...foreground, a: 1 }),
      explicit: Boolean(surfaceOverride || localSurface || configured),
      reason: "opaque",
    };
  }

  if (sectionHasBackgroundImage || sectionBackgroundComplex) {
    return { resolved: false, color: "", explicit: Boolean(surfaceOverride || localSurface || configured), reason: "complex-underlay" };
  }
  const underlay = parseCssColor(sectionBackgroundColor);
  if (!underlay || underlay.a < 0.999) {
    return { resolved: false, color: "", explicit: Boolean(surfaceOverride || localSurface || configured), reason: "unknown-underlay" };
  }
  const mixed = composite(foreground, underlay);
  return {
    resolved: Boolean(mixed),
    color: opaqueCss(mixed),
    explicit: Boolean(surfaceOverride || localSurface || configured),
    reason: mixed ? "composited" : "unknown-underlay",
  };
}

function correctedText(candidate, background, minimumContrast = MIN_READABLE_CONTRAST) {
  const foreground = text(candidate);
  if (!foreground || !background) return { color: foreground, adjusted: false };
  const ratio = contrastRatio(foreground, background);
  if (ratio === null || ratio >= minimumContrast) return { color: foreground, adjusted: false };
  const color = ensureReadableTextColor(foreground, background, minimumContrast);
  return { color, adjusted: text(color).toLowerCase() !== foreground.toLowerCase() };
}

function resolveOwnSurface(background, cardSurface) {
  const candidate = text(background);
  const parsed = parseCssColor(candidate);
  if (!parsed) return { resolved: false, color: "" };
  if (parsed.a >= 0.999) return { resolved: true, color: opaqueCss({ ...parsed, a: 1 }) };
  if (!cardSurface?.resolved) return { resolved: false, color: "" };
  const underlay = parseCssColor(cardSurface.color);
  if (!underlay) return { resolved: false, color: "" };
  const mixed = composite(parsed, underlay);
  return { resolved: Boolean(mixed), color: opaqueCss(mixed) };
}

function baseTextCandidates(cardConfig, palette, visualStyle) {
  const local = localPalette(visualStyle, palette);
  const base = {
    type: text(cardConfig?.typeColor) || palette.primary,
    title: text(cardConfig?.titleColor) || palette.text,
    category: text(cardConfig?.categoryColor) || palette.mutedText,
    subcategory: text(cardConfig?.subcategoryColor) || palette.mutedText,
    description: text(cardConfig?.descriptionColor) || palette.mutedText,
    priceLabel: text(cardConfig?.priceColor) || palette.mutedText,
    priceValue: text(cardConfig?.priceColor) || palette.secondary,
  };
  if (!local) return base;
  return {
    type: local.primary,
    title: local.secondary,
    category: local.mutedText,
    subcategory: local.mutedText,
    description: local.mutedText,
    priceLabel: local.text,
    priceValue: local.text,
  };
}

const TEXT_PATHS = Object.freeze({
  type: "catalog.cards.typeColor",
  title: "catalog.cards.titleColor",
  category: "catalog.cards.categoryColor",
  subcategory: "catalog.cards.subcategoryColor",
  description: "catalog.cards.descriptionColor",
  priceLabel: "catalog.cards.priceColor",
  priceValue: "catalog.cards.priceColor",
});

function explicitTextField(cardConfig, role) {
  const fields = {
    type: "typeColor",
    title: "titleColor",
    category: "categoryColor",
    subcategory: "subcategoryColor",
    description: "descriptionColor",
    priceLabel: "priceColor",
    priceValue: "priceColor",
  };
  return Boolean(text(cardConfig?.[fields[role]]));
}

function resolveCta(path, cardConfig, palette, visualStyle, cardSurface) {
  const local = localPalette(visualStyle, palette);
  const override = elementStyle(visualStyle, path);
  const configuredBackground = text(cardConfig?.buttonBackgroundColor);
  const configuredHoverBackground = text(cardConfig?.buttonHoverBackgroundColor);
  const configuredText = text(cardConfig?.buttonTextColor);
  const configuredHoverText = text(cardConfig?.buttonHoverTextColor);

  const background = text(override.background) || (local ? local.primary : configuredBackground || palette.primary);
  const hoverBackground = text(override.hoverBackground) || (local ? local.secondary : configuredHoverBackground || configuredBackground || palette.primary);
  const foreground = text(override.color) || (local ? local.onPrimary : configuredText || palette.onPrimary);
  const hoverForeground = text(override.hoverColor) || (local ? local.onSecondary : configuredHoverText || configuredText || palette.onPrimary);

  const normalSurface = resolveOwnSurface(background, cardSurface);
  const hoverSurface = resolveOwnSurface(hoverBackground, cardSurface);
  const normal = normalSurface.resolved ? correctedText(foreground, normalSurface.color) : { color: foreground, adjusted: false };
  const hover = hoverSurface.resolved ? correctedText(hoverForeground, hoverSurface.color) : { color: hoverForeground, adjusted: false };
  const explicit = Boolean(
    text(override.background) || text(override.hoverBackground) || text(override.color) || text(override.hoverColor) ||
    configuredBackground || configuredHoverBackground || configuredText || configuredHoverText || local
  );

  return {
    background,
    hoverBackground,
    color: normal.color,
    hoverColor: hover.color,
    normalAdjusted: normal.adjusted,
    hoverAdjusted: hover.adjusted,
    adjusted: normal.adjusted || hover.adjusted,
    explicitCorrection: explicit && (normal.adjusted || hover.adjusted),
  };
}

export function resolveCatalogCardContrast({
  cardConfig = {},
  palette = {},
  visualStyle = {},
  cardBackgroundImageVisible = false,
  sectionBackgroundColor = "",
  sectionBackgroundComplex = false,
  sectionHasBackgroundImage = false,
} = {}) {
  const safePalette = {
    primary: paletteValue(palette, "primary", "#F35A3A"),
    secondary: paletteValue(palette, "secondary", "#19242F"),
    background: paletteValue(palette, "background", "#EEF0F5"),
    surface: paletteValue(palette, "surface", "#FFFFFF"),
    text: paletteValue(palette, "text", "#1A1A2E"),
    mutedText: paletteValue(palette, "mutedText", "#5B5D73"),
    onPrimary: paletteValue(palette, "onPrimary", DEFAULT_LIGHT_TEXT),
    onSecondary: paletteValue(palette, "onSecondary", DEFAULT_LIGHT_TEXT),
  };

  const cardSurface = effectiveCardSurface({
    cardConfig,
    palette: safePalette,
    visualStyle,
    cardBackgroundImageVisible,
    sectionBackgroundColor: sectionBackgroundColor || safePalette.background,
    sectionBackgroundComplex,
    sectionHasBackgroundImage,
  });
  const base = baseTextCandidates(cardConfig, safePalette, visualStyle);
  const local = visualStyle?.useGlobalColors === false;
  const textColors = {};
  const textHoverColors = {};
  const textAdjusted = {};
  const textHoverAdjusted = {};
  const corrections = [];
  let explicitCorrection = false;

  Object.entries(TEXT_PATHS).forEach(([role, path]) => {
    const style = elementStyle(visualStyle, path);
    const override = text(style.color);
    const hoverOverride = text(style.hoverColor);
    const candidate = override || base[role];
    const hoverCandidate = hoverOverride || candidate;
    const normalSurface = text(style.background) ? resolveOwnSurface(style.background, cardSurface) : cardSurface;
    const hoverSurface = text(style.hoverBackground)
      ? resolveOwnSurface(style.hoverBackground, cardSurface)
      : normalSurface;
    const result = normalSurface.resolved ? correctedText(candidate, normalSurface.color) : { color: candidate, adjusted: false };
    const hoverResult = hoverSurface.resolved ? correctedText(hoverCandidate, hoverSurface.color) : { color: hoverCandidate, adjusted: false };
    textColors[role] = result.color;
    textHoverColors[role] = hoverResult.color;
    textAdjusted[role] = result.adjusted;
    textHoverAdjusted[role] = hoverResult.adjusted;
    if (!result.adjusted && !hoverResult.adjusted) return;
    corrections.push(path);
    if (
      override || hoverOverride || text(style.background) || text(style.hoverBackground) ||
      explicitTextField(cardConfig, role) || local || cardSurface.explicit
    ) explicitCorrection = true;
  });

  const cta = resolveCta("catalog.cards.ctaLabel", cardConfig, safePalette, visualStyle, cardSurface);
  const serviceCta = resolveCta("catalog.cards.serviceCtaLabel", cardConfig, safePalette, visualStyle, cardSurface);
  if (cta.adjusted) corrections.push("catalog.cards.ctaLabel");
  if (serviceCta.adjusted) corrections.push("catalog.cards.serviceCtaLabel");
  explicitCorrection = explicitCorrection || cta.explicitCorrection || serviceCta.explicitCorrection;

  return {
    surface: cardSurface,
    text: textColors,
    textHover: textHoverColors,
    textAdjusted,
    textHoverAdjusted,
    cta,
    serviceCta,
    corrections: [...new Set(corrections)],
    hasCorrection: corrections.length > 0,
    hasExplicitCorrection: explicitCorrection,
  };
}

export function catalogCardContrastSx(resolution) {
  const value = resolution || {};
  const textColors = value.text || {};
  const textHoverColors = value.textHover || {};
  const adjusted = value.textAdjusted || {};
  const hoverAdjusted = value.textHoverAdjusted || {};
  const important = (color) => color ? `${color} !important` : undefined;
  const sx = {};
  const addText = (role, normalStyles, hoverStyles = null) => {
    const selector = `&& [data-card-contrast-role="${role}"]`;
    if (adjusted[role]) sx[selector] = normalStyles;
    if (hoverAdjusted[role]) sx[`${selector}:hover`] = hoverStyles || { color: important(textHoverColors[role]) };
  };
  addText("type", { color: important(textColors.type) });
  addText("title", { color: important(textColors.title) });
  addText("category", { color: important(textColors.category), borderColor: important(textColors.category) }, { color: important(textHoverColors.category), borderColor: important(textHoverColors.category) });
  addText("subcategory", { color: important(textColors.subcategory) });
  addText("description", { color: important(textColors.description) });
  addText("price-label", { color: important(textColors.priceLabel) });
  addText("price-value", { color: important(textColors.priceValue) });
  if (value.cta?.normalAdjusted) sx['&& [data-landing-field="catalog.cards.ctaLabel"]'] = { color: important(value.cta.color) };
  if (value.cta?.hoverAdjusted) sx['&& [data-landing-field="catalog.cards.ctaLabel"]:hover'] = { color: important(value.cta.hoverColor) };
  if (value.serviceCta?.normalAdjusted) sx['&& [data-landing-field="catalog.cards.serviceCtaLabel"]'] = { color: important(value.serviceCta.color) };
  if (value.serviceCta?.hoverAdjusted) sx['&& [data-landing-field="catalog.cards.serviceCtaLabel"]:hover'] = { color: important(value.serviceCta.hoverColor) };
  return sx;
}
